/**
 * Surface frames for the geometry editor.
 *
 * A frame is what decides how a texture lands on a surface: the plane
 * (a·x + b·y + c·z + d = 0), the texture's vertical and horizontal directions
 * in that plane, and the origin its tile grid starts from. The editor draws the
 * frame as a tile grid over the photo with the camera the renderer uses, and
 * sends the edited frame back to be applied as is.
 *
 * Camera convention (same as py-visualizer and plane-depth-estimator): pixel
 * (u, v) looks along (-(u-cx)/fx, -(v-cy)/fy, 1); a 3D point projects to
 * u = cx - fx·X/Z, v = cy - fy·Y/Z.
 */

export type Vec3 = [number, number, number];
export type Plane = [number, number, number, number];

export interface Intrinsics { fx: number; fy: number; cx: number; cy: number }

export interface Frame {
  plane: Plane;
  vertical: Vec3;
  horizontal: Vec3;
  origin: Vec3;
}

/** Edits relative to the analysed frame, all rotations in degrees. */
export interface FrameEdit {
  tilt: number;     // about the surface's left-right line on screen
  turn: number;     // about the in-plane line perpendicular to it
  pattern: number;  // spins the texture in the plane
  size: number;     // plane distance factor: <1 = closer = bigger tiles
}

export const NO_EDIT: FrameEdit = { tilt: 0, turn: 0, pattern: 0, size: 1 };

// ------------------------------------------------------------------ vectors
export const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];
export const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0],
];
export const norm = (a: Vec3) => Math.sqrt(dot(a, a));
export const unit = (a: Vec3): Vec3 => scale(a, 1 / (norm(a) || 1));

/** Rotate v about a unit axis by angle (radians), Rodrigues. */
export function rotate(v: Vec3, axis: Vec3, angle: number): Vec3 {
  const k = unit(axis);
  const c = Math.cos(angle), s = Math.sin(angle);
  return add(add(scale(v, c), scale(cross(k, v), s)), scale(k, dot(k, v) * (1 - c)));
}

const deg = (d: number) => (d * Math.PI) / 180;

// ------------------------------------------------------------------- camera
export function ray(u: number, v: number, K: Intrinsics): Vec3 {
  return [-(u - K.cx) / K.fx, -(v - K.cy) / K.fy, 1];
}

/** Where a pixel's ray meets the plane, or null (parallel / behind camera). */
export function unproject(u: number, v: number, plane: Plane, K: Intrinsics): Vec3 | null {
  const r = ray(u, v, K);
  const n: Vec3 = [plane[0], plane[1], plane[2]];
  const den = dot(n, r);
  if (Math.abs(den) < 1e-9) return null;
  const t = -plane[3] / den;
  return t > 0 ? scale(r, t) : null;
}

export function project(p: Vec3, K: Intrinsics): [number, number] | null {
  if (p[2] <= 1e-6) return null;
  return [K.cx - (K.fx * p[0]) / p[2], K.cy - (K.fy * p[1]) / p[2]];
}

// ------------------------------------------------------------------- edits
/**
 * Axes the tilt and turn sliders rotate about. Defined from the camera, not
 * the texture directions, so spinning the pattern never changes what "tilt"
 * does: tilt turns about the surface's left-right line on screen (camera X
 * projected into the plane), turn about the in-plane line perpendicular to it.
 */
export function editAxes(plane: Plane): { tiltAxis: Vec3; turnAxis: Vec3; normal: Vec3 } {
  const normal = unit([plane[0], plane[1], plane[2]]);
  let tiltAxis = sub([1, 0, 0], scale(normal, normal[0]));
  if (norm(tiltAxis) < 1e-6) tiltAxis = sub([0, 1, 0], scale(normal, normal[1]));
  tiltAxis = unit(tiltAxis);
  return { tiltAxis, turnAxis: unit(cross(normal, tiltAxis)), normal };
}

/**
 * The frame with its origin moved straight onto its plane. Analysed origins
 * can sit a little off the plane (the origin is set before the plane is
 * snapped level/vertical). Texture coordinates only use the in-plane part of
 * (point - origin), so moving the origin along the normal changes nothing the
 * renderer computes — it just makes the frame exact.
 */
export function onPlane(frame: Frame): Frame {
  const n: Vec3 = [frame.plane[0], frame.plane[1], frame.plane[2]];
  const len = norm(n) || 1;
  const off = (dot(n, frame.origin) + frame.plane[3]) / (len * len);
  return { ...frame, origin: sub(frame.origin, scale(n, off)) };
}

/**
 * The frame after an edit, pivoting on `pivot` (the surface's centre on the
 * analysed plane). Rotating about a point ON the surface keeps that point
 * where it is, so the tile size at the centre of the surface does not jump —
 * the old editor rotated about an off-surface point and the whole plane moved
 * nearer or further. `size` then scales the plane along the camera rays: the
 * outline stays put and every tile grows or shrinks by 1/size.
 */
export function applyEdit(rawBase: Frame, edit: FrameEdit, pivot: Vec3): Frame {
  const base = onPlane(rawBase);
  const { tiltAxis, turnAxis } = editAxes(base.plane);
  const rot = (v: Vec3) => rotate(rotate(v, tiltAxis, deg(edit.tilt)), turnAxis, deg(edit.turn));

  let normal = rot(unit([base.plane[0], base.plane[1], base.plane[2]]));
  let vertical = rot(base.vertical);
  let horizontal = rot(base.horizontal);
  let origin = add(rot(sub(base.origin, pivot)), pivot);

  if (edit.pattern) {
    vertical = rotate(vertical, normal, deg(edit.pattern));
    horizontal = rotate(horizontal, normal, deg(edit.pattern));
  }

  // Re-orthonormalize (keeps float drift out of the frame the server
  // validates), keeping the horizontal direction the rotation produced so
  // the texture is not mirrored
  normal = unit(normal);
  vertical = unit(sub(vertical, scale(normal, dot(vertical, normal))));
  const rotatedHorizontal = horizontal;
  horizontal = unit(cross(vertical, normal));
  if (dot(horizontal, rotatedHorizontal) < 0) horizontal = scale(horizontal, -1);

  const f = edit.size > 0 ? edit.size : 1;
  origin = scale(origin, f);
  const pivotScaled = scale(pivot, f);
  const d = -dot(normal, pivotScaled);
  return { plane: [normal[0], normal[1], normal[2], d], vertical, horizontal, origin };
}

// --------------------------------------------------------------- tile grid
export interface GridLine { a: [number, number]; b: [number, number]; major: boolean }

/**
 * Tile-grid lines of a frame, in pixels, covering the given outline points.
 * Lines are u = k·tile and v = k·tile in texture coordinates measured from
 * the frame origin (plus the surface's texture offset), exactly where the
 * renderer starts tiles; every 5th line is "major".
 */
export function gridLines(
  frame: Frame, K: Intrinsics, outline: [number, number][], tile: number,
  offset: [number, number] = [0, 0], maxLines = 160,
): GridLine[] {
  const uvs: [number, number][] = [];
  for (const [x, y] of outline) {
    const p = unproject(x, y, frame.plane, K);
    if (!p) continue;
    const rel = sub(p, frame.origin);
    uvs.push([dot(rel, frame.horizontal) + offset[0], dot(rel, frame.vertical) + offset[1]]);
  }
  if (uvs.length < 2 || !(tile > 0)) return [];
  const [u0, u1] = [Math.min(...uvs.map((q) => q[0])), Math.max(...uvs.map((q) => q[0]))];
  const [v0, v1] = [Math.min(...uvs.map((q) => q[1])), Math.max(...uvs.map((q) => q[1]))];
  if ((u1 - u0) / tile + (v1 - v0) / tile > maxLines) return []; // absurd scale: draw nothing rather than freeze

  const toPixel = (u: number, v: number) =>
    project(add(frame.origin, add(scale(frame.horizontal, u - offset[0]), scale(frame.vertical, v - offset[1]))), K);
  const lines: GridLine[] = [];
  const push = (p: [number, number] | null, q: [number, number] | null, major: boolean) => {
    if (p && q) lines.push({ a: p, b: q, major });
  };
  for (let k = Math.ceil(u0 / tile); k * tile <= u1; k++) push(toPixel(k * tile, v0), toPixel(k * tile, v1), k % 5 === 0);
  for (let k = Math.ceil(v0 / tile); k * tile <= v1; k++) push(toPixel(u0, k * tile), toPixel(u1, k * tile), k % 5 === 0);
  return lines;
}

/** The analysed surface centre in 3D: its centroid pixel on its plane. */
export function pivotOf(frame: Frame, centroid: [number, number], K: Intrinsics): Vec3 {
  return unproject(centroid[0], centroid[1], frame.plane, K) ?? frame.origin;
}

/** Angle in degrees between two planes' normals (for "changed by X°"). */
export function angleBetween(a: Plane, b: Plane): number {
  const na = unit([a[0], a[1], a[2]]), nb = unit([b[0], b[1], b[2]]);
  return (Math.acos(Math.min(1, Math.abs(dot(na, nb)))) * 180) / Math.PI;
}
